import * as _ from 'lodash';
import * as moment from 'moment';
import { Component, FormEvent } from 'react';
import { Dispatch, connect } from 'react-redux';
import {
    AppActionCreators, IApplicationState, ISerologyFinalXmBloodDrawsResponse, ReportActionCreators,
    FileActionCreators, ActionCenterActionCreators
} from '@nkr/store/index';
import { ISerologyFinalXmBloodDraw } from '@nkr/models/index';
import { Button, FormControl, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Grid from './grid';
import { Utility } from '@nkr/services/utility';
import { downloadFile } from '@nkr/services/download-file';
import { FileDocumentType } from '@nkr/enums/document-type';

type TMode = 'serology' | 'finalxm';

interface IOwnProps {
    isCenterLogin: boolean,
    centerId: number
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSerologyFinalXmBloodDraws: (centerId: number, startDate: string) => Promise<ISerologyFinalXmBloodDrawsResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    getFinalXmResults: (recipientId: number, donorId: number) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: ISerologyFinalXmBloodDraw[],
    startDate: string,
    mode: TMode
}

class SerologyFinalXmBloodDraws extends Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        mode: 'serology'
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting serology and final XM blood draw records, one moment please...').then(() => {
            this.props.getSerologyFinalXmBloodDraws(this.props.centerId, this.state.startDate).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    handleMonthChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let selected = moment(e.currentTarget.value, 'YYYY-MM-DD');

        this.setState({
            startDate: selected.format('YYYY-MM-DD')
        }, () => {
            this.getData();
        });
    }

    handleModeChange = (mode: TMode) => {
        this.setState({ mode });
    }

    handleViewResults = (row: ISerologyFinalXmBloodDraw) => {
        if (this.state.mode == 'serology') {
            this.props.downloadFile(FileDocumentType.DonorSerology, row.donorId).then((response: any) => {
                downloadFile(response.data, `serology_${row.donorAlias}.pdf`, response.headers?.contentType || 'application/pdf');
            }).catch(() => {
                toast.error('Unable to download serology results at this time. Please contact support.');
            });
        } else {
            this.props.getFinalXmResults(row.recipientId, row.donorId).then((response: any) => {
                downloadFile(response.data, `finalxm_${row.donorAlias}.pdf`, 'application/pdf');
            }).catch(() => {
                toast.error('Unable to download final XM results at this time. Please contact support.');
            });
        }
    }

    handleExport = () => {
        let currentMonth = moment(this.state.startDate);
        let monthYear = currentMonth.format('MM-YYYY');
        let mode = this.state.mode;

        let csvContent = 'data:text/csv;charset=utf-8,'
            + 'Donor Center,Swap,Donor Alias,Blood Draw Date,Upload Date,Donor Surgery Date,Recipient Surgery Date,Delta (Days)\n'
            + this.state.data.map(e => e.donorCenterAlias
                + ',' + e.swapNumber
                + ',' + e.donorAlias
                + ',' + e.bloodDrawDate
                + ',' + (mode == 'serology' ? e.serologyUploadDate : e.finalXmUploadDate)
                + ',' + e.donorSurgeryDate
                + ',' + e.recipientSurgeryDate
                + ',' + (e.deltaDays != null ? e.deltaDays : '')).join('\n');

        let encodedUri = encodeURI(csvContent);

        let link = document.createElement('a');

        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `serology-final-xm-blood-draws_${monthYear}.csv`);

        document.body.appendChild(link);

        link.click();
    }

    render = () => {
        const dateList = Utility.getRange(moment().subtract(11, 'month'), moment().add(1, 'month'), 'month').map(d => d.date(1));

        return (
            <>
                <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                        <span
                            style={{ cursor: 'pointer', fontWeight: this.state.mode == 'serology' ? 700 : 400, color: this.state.mode == 'serology' ? '#000' : '#428bca' }}
                            onClick={() => this.handleModeChange('serology')}>
                            Serology
                        </span>
                        <span style={{ margin: '0 10px', color: '#ccc' }}>|</span>
                        <span
                            style={{ cursor: 'pointer', fontWeight: this.state.mode == 'finalxm' ? 700 : 400, color: this.state.mode == 'finalxm' ? '#000' : '#428bca' }}
                            onClick={() => this.handleModeChange('finalxm')}>
                            Final XM
                        </span>

                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 25 }}>
                            <div style={{ marginRight: 5 }}>Donor Surgery Month:</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    bsSize='sm'
                                    value={this.state.startDate}
                                    onChange={this.handleMonthChange}>
                                    {dateList.map(date =>
                                        <option key={`ctr-${date.format('YYYY-MM-DD')}`} value={date.format('YYYY-MM-DD')}>{date.format('MM/YYYY')}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Button bsSize='sm' onClick={this.handleExport}>Export to CSV</Button>
                    </div>
                </Well>

                <Grid data={this.state.data} mode={this.state.mode} onViewResults={this.handleViewResults} />
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSerologyFinalXmBloodDraws: (centerId, startDate) => {
            return dispatch(ReportActionCreators.getSerologyFinalXmBloodDraws(centerId, startDate));
        },
        downloadFile: (docType, lookupId) => {
            return dispatch(FileActionCreators.downloadFile(docType, lookupId));
        },
        getFinalXmResults: (recipientId, donorId) => {
            return dispatch(ActionCenterActionCreators.getFinalXmResults(recipientId, donorId));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(SerologyFinalXmBloodDraws);
